import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Settings, User, Crown, CreditCard, Moon, Sun, Globe, ChevronRight, Shield, LogOut, BarChart3, Sparkles, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";

const API = process.env.REACT_APP_BACKEND_URL;
const FREE_ANALYSIS_LIMIT = 5;

export default function SettingsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [editName, setEditName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [dreamMode, setDreamMode] = useState(() => localStorage.getItem("dreamMode") === "true");
  const [loading, setLoading] = useState(true);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    fetchUserData();
    fetchUsageData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(res.data);
      setEditName(res.data.name || "");
    } catch (err) {
      toast.error(t("settings.fetchError") || "Virhe tietojen haussa");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/auth/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageData(res.data);
    } catch (err) {
      console.error("Usage fetch error:", err);
    }
  };

  const handleUpdateName = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/api/auth/profile`, { name: editName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(prev => ({ ...prev, name: editName }));
      setIsEditing(false);
      toast.success(t("settings.nameUpdated") || "Nimi p\u00e4ivitetty!");
    } catch (err) {
      toast.error(t("settings.updateError") || "P\u00e4ivitys ep\u00e4onnistui");
    }
  };

  const handleCustomerPortal = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/api/payments/customer-portal`, 
        { return_url: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.url) {
        window.open(res.data.url, "_blank");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t("settings.portalError") || "Tilauksen hallinta ei ole saatavilla";
      toast.error(msg);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error(t("settings.fillAllFields") || "T\u00e4yt\u00e4 kaikki kent\u00e4t");
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t("settings.passwordTooShort") || "Salasanan pit\u00e4\u00e4 olla v\u00e4hint\u00e4\u00e4n 6 merkki\u00e4");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("settings.passwordMismatch") || "Salasanat eiv\u00e4t t\u00e4sm\u00e4\u00e4");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("settings.passwordChanged") || "Salasana vaihdettu!");
      setShowPasswordForm(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const msg = err.response?.data?.detail || t("settings.passwordError") || "Salasanan vaihto ep\u00e4onnistui";
      toast.error(msg);
    }
  };

  const toggleDreamMode = () => {
    const newMode = !dreamMode;
    setDreamMode(newMode);
    localStorage.setItem("dreamMode", newMode.toString());
    document.documentElement.classList.toggle("dark", newMode);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50/80 via-white to-blue-50/50 flex items-center justify-center">
        <div className="animate-pulse text-void/50">{t("common.loading") || "Ladataan..."}</div>
      </div>
    );
  }

  const analysisUsed = userData?.ai_analysis_count || 0;
  const isPremium = userData?.is_premium;
  const analysisPercent = isPremium ? 100 : Math.min((analysisUsed / FREE_ANALYSIS_LIMIT) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/80 via-white to-blue-50/50">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-void/60 hover:text-void transition-colors">
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-void/70" />
              <h1 className="text-lg font-heading font-medium text-void" data-testid="settings-title">
                {t("settings.title") || "Asetukset"}
              </h1>
            </div>
          </div>
          <Button variant="ghost" onClick={onLogout} className="text-void/60 hover:text-void" data-testid="settings-logout">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {/* 1. Profile Card */}
        <div className="bg-white/70 rounded-2xl p-6 border border-white/60" data-testid="profile-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-heading font-medium text-void">
              {t("settings.profile") || "Profiili"}
            </h2>
          </div>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm text-void/50 block mb-1">{t("settings.name") || "Nimi"}</label>
              {isEditing ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-void text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    data-testid="edit-name-input"
                  />
                  <Button onClick={handleUpdateName} className="bg-void text-white hover:bg-void/90 rounded-lg text-sm" data-testid="save-name-button">
                    {t("common.save") || "Tallenna"}
                  </Button>
                  <Button variant="ghost" onClick={() => { setIsEditing(false); setEditName(userData?.name || ""); }} className="text-sm">
                    {t("common.cancel") || "Peruuta"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-void font-medium" data-testid="user-name">{userData?.name}</span>
                  <button onClick={() => setIsEditing(true)} className="text-sm text-purple-500 hover:text-purple-700 transition-colors" data-testid="edit-name-button">
                    {t("settings.edit") || "Muokkaa"}
                  </button>
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="text-sm text-void/50 block mb-1">{t("settings.email") || "S\u00e4hk\u00f6posti"}</label>
              <span className="text-void" data-testid="user-email">{userData?.email}</span>
            </div>

            {/* Member since */}
            <div>
              <label className="text-sm text-void/50 block mb-1">{t("settings.memberSince") || "J\u00e4sen alkaen"}</label>
              <span className="text-void" data-testid="member-since">
                {userData?.created_at ? new Date(userData.created_at).toLocaleDateString("fi-FI") : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* 2. Subscription Status Card */}
        <div className={`rounded-2xl p-6 border ${isPremium ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200/60" : "bg-white/70 border-white/60"}`} data-testid="subscription-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isPremium ? (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-300 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-200 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h2 className="font-heading font-medium text-void" data-testid="subscription-status">
                  {isPremium ? (t("settings.premium") || "Premium") : (t("settings.freemium") || "Freemium")}
                </h2>
                <p className="text-sm text-void/50">
                  {isPremium && userData?.premium_type === "lifetime"
                    ? (t("settings.lifetime") || "Ikuinen tilaus")
                    : isPremium && userData?.premium_type === "monthly"
                    ? (t("settings.monthly") || "Kuukausitilaus")
                    : (t("settings.freeAccount") || "Ilmainen tili")}
                </p>
              </div>
            </div>
            {!isPremium && (
              <Button
                onClick={() => navigate("/premium")}
                className="bg-gradient-to-r from-amber-400 to-yellow-300 text-void hover:from-amber-500 hover:to-yellow-400 rounded-full text-sm font-medium"
                data-testid="upgrade-button"
              >
                {t("settings.upgrade") || "P\u00e4ivit\u00e4"}
              </Button>
            )}
          </div>

          <div className="space-y-2 text-sm">
            {isPremium && (
              <>
                <div className="flex justify-between">
                  <span className="text-void/50">{t("settings.plan") || "Tilaus"}</span>
                  <span className="text-void font-medium">
                    {userData?.premium_type === "lifetime" 
                      ? (t("settings.lifetimePlan") || "Lifetime Premium") 
                      : (t("settings.monthlyPlan") || "Premium / kk")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-void/50">{t("settings.aiQuota") || "AI-analyysit"}</span>
                  <span className="text-void font-medium text-emerald-600">{t("settings.unlimited") || "Rajaton"}</span>
                </div>
                <div className="pt-2">
                  <button
                    onClick={handleCustomerPortal}
                    className="w-full py-2.5 rounded-xl border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    data-testid="manage-subscription"
                  >
                    <CreditCard className="w-4 h-4" />
                    {t("settings.manageSubscription") || "Hallitse tilausta"}
                  </button>
                </div>
              </>
            )}
            {!isPremium && (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-void/50">{t("settings.aiQuota") || "AI-analyysit"}</span>
                  <span className={`font-medium ${analysisUsed >= FREE_ANALYSIS_LIMIT ? 'text-red-500' : 'text-void'}`}>
                    {analysisUsed} / {FREE_ANALYSIS_LIMIT}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-500 ${
                      analysisPercent >= 100
                        ? "bg-gradient-to-r from-red-400 to-red-500"
                        : analysisPercent >= 60
                        ? "bg-gradient-to-r from-amber-400 to-yellow-400"
                        : "bg-gradient-to-r from-purple-400 to-indigo-400"
                    }`}
                    style={{ width: `${analysisPercent}%` }}
                    data-testid="analysis-progress-bar"
                  />
                </div>
                {analysisUsed >= FREE_ANALYSIS_LIMIT && (
                  <p className="text-sm text-red-500">
                    {t("settings.limitReached") || "Ilmaiset analyysit k\u00e4ytetty. P\u00e4ivit\u00e4 Premiumiin!"}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 3. Service Usage Card */}
        <div className="bg-white/70 rounded-2xl p-6 border border-white/60" data-testid="usage-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-medium text-void">
                {t("settings.usedServices") || "K\u00e4ytetyt palvelut"}
              </h2>
              <p className="text-sm text-void/50">
                {t("settings.usedServicesDesc") || "Palvelujen k\u00e4ytt\u00f6tilastot"}
              </p>
            </div>
          </div>

          {usageData ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-purple-50/60 rounded-xl p-3.5" data-testid="usage-analyses">
                <p className="text-2xl font-heading font-semibold text-purple-600">{usageData.ai_analyses || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcAnalyses") || "AI-analyysit"}</p>
              </div>
              <div className="bg-pink-50/60 rounded-xl p-3.5" data-testid="usage-art">
                <p className="text-2xl font-heading font-semibold text-pink-600">{usageData.art_generated || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcArt") || "Unitaidekuvat"}</p>
              </div>
              <div className="bg-blue-50/60 rounded-xl p-3.5" data-testid="usage-dreams">
                <p className="text-2xl font-heading font-semibold text-blue-600">{usageData.total_dreams || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcDreams") || "Unet tallennettu"}</p>
              </div>
              <div className="bg-emerald-50/60 rounded-xl p-3.5" data-testid="usage-summaries">
                <p className="text-2xl font-heading font-semibold text-emerald-600">{usageData.summaries_generated || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcSummaries") || "Uniyhteenvedot"}</p>
              </div>
              <div className="bg-amber-50/60 rounded-xl p-3.5" data-testid="usage-public">
                <p className="text-2xl font-heading font-semibold text-amber-600">{usageData.public_dreams || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcPublic") || "Julkiset unet"}</p>
              </div>
              <div className="bg-indigo-50/60 rounded-xl p-3.5" data-testid="usage-analyzed">
                <p className="text-2xl font-heading font-semibold text-indigo-600">{usageData.analyzed_dreams || 0}</p>
                <p className="text-xs text-void/50 mt-0.5">{t("settings.svcAnalyzed") || "Analysoidut unet"}</p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-void/40 animate-pulse">{t("common.loading") || "Ladataan..."}</div>
          )}
        </div>

        {/* 4. Preferences & Security Card (combined) */}
        <div className="bg-white/70 rounded-2xl p-6 border border-white/60" data-testid="preferences-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-heading font-medium text-void">
              {t("settings.preferences") || "Asetukset"}
            </h2>
          </div>

          <div className="space-y-5">
            {/* Language */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-void font-medium text-sm">{t("settings.language") || "Kieli"}</p>
                <p className="text-xs text-void/50">{t("settings.languageDesc") || "Sovelluksen kieli"}</p>
              </div>
              <LanguageSelector variant="minimal" />
            </div>

            <div className="border-t border-slate-100" />

            {/* Dream Mode */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-void font-medium text-sm">{t("settings.dreamMode") || "Unitila"}</p>
                <p className="text-xs text-void/50">{t("settings.dreamModeDesc") || "Tumma tila mukavaan y\u00f6k\u00e4ytt\u00f6\u00f6n"}</p>
              </div>
              <button
                onClick={toggleDreamMode}
                className={`w-12 h-7 rounded-full transition-all duration-300 flex items-center px-1 ${
                  dreamMode ? "bg-indigo-500" : "bg-slate-200"
                }`}
                data-testid="dream-mode-toggle"
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-md flex items-center justify-center transition-transform duration-300 ${
                  dreamMode ? "translate-x-5" : "translate-x-0"
                }`}>
                  {dreamMode ? <Moon className="w-3 h-3 text-indigo-500" /> : <Sun className="w-3 h-3 text-amber-400" />}
                </div>
              </button>
            </div>

            <div className="border-t border-slate-100" />

            {/* Change Password */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-void/40" />
                <p className="text-void font-medium text-sm">{t("settings.changePassword") || "Vaihda salasana"}</p>
              </div>
              {!showPasswordForm ? (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="text-sm text-purple-500 hover:text-purple-700 transition-colors"
                  data-testid="change-password-button"
                >
                  {t("settings.changePassword") || "Vaihda salasana"}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <label className="text-sm text-void/50 block mb-1">{t("settings.currentPassword") || "Nykyinen salasana"}</label>
                    <div className="flex items-center">
                      <input
                        type={showCurrentPw ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-void text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                        data-testid="current-password-input"
                      />
                      <button onClick={() => setShowCurrentPw(!showCurrentPw)} className="ml-2 text-void/40 hover:text-void/70">
                        {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <label className="text-sm text-void/50 block mb-1">{t("settings.newPassword") || "Uusi salasana"}</label>
                    <div className="flex items-center">
                      <input
                        type={showNewPw ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-void text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                        data-testid="new-password-input"
                      />
                      <button onClick={() => setShowNewPw(!showNewPw)} className="ml-2 text-void/40 hover:text-void/70">
                        {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-void/50 block mb-1">{t("settings.confirmPassword") || "Vahvista salasana"}</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-void text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button onClick={handleChangePassword} className="bg-void text-white hover:bg-void/90 rounded-lg text-sm" data-testid="save-password-button">
                      {t("common.save") || "Tallenna"}
                    </Button>
                    <Button variant="ghost" onClick={() => { setShowPasswordForm(false); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }} className="text-sm">
                      {t("common.cancel") || "Peruuta"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={onLogout}
          className="w-full py-3 rounded-2xl border border-red-200 text-red-500 hover:bg-red-50 transition-colors font-medium text-sm"
          data-testid="settings-logout-bottom"
        >
          {t("settings.logout") || "Kirjaudu ulos"}
        </button>
      </div>
    </div>
  );
}
